import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTruck, FiShield, FiAward, FiUsers, FiClock, FiPhone, FiCheckCircle, FiMapPin, FiExternalLink } = FiIcons;

const About = () => {
  const stats = [
    { number: "500+", label: "Trucks Serviced" },
    { number: "5+", label: "Years Experience" },
    { number: "Available", label: "Emergency Service" },
    { number: "100%", label: "Satisfaction Rate" }
  ];

  const certifications = [
    "CARB Compliance Specialists",
    "EPA Regulatory Experts",
    "Clean Truck Check Pros",
    "Fleet Management Experts"
  ];

  const values = [
    { icon: FiShield, title: "Reliability", description: "Dependable service you can count on for critical compliance needs." },
    { icon: FiClock, title: "Timeliness", description: "We understand time is money - fast, efficient service every time." },
    { icon: FiUsers, title: "Expertise", description: "Skilled technicians with years of CARB compliance experience." },
    { icon: FiAward, title: "Quality", description: "Premium service and equipment for superior results." }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-blue-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/76f068f9e1e56b57a7d668937e51e748.jpeg" 
            alt="Business planning and logistics" 
            className="w-full h-full object-cover opacity-30" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              About Ramirez Mobile Clean Truck
            </motion.h1>
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl mb-8"
            >
              Your Trusted CARB Compliance Partner in Ontario, California
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-blue-100"
            >
              Dedicated to keeping your diesel fleet compliant, efficient, and on the road.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
              <h3 className="text-2xl text-gray-600 mb-6">
                Built on Experience, Driven by Excellence
              </h3>
              <div className="space-y-6 text-gray-600">
                <p>
                  Ramirez Mobile Clean Truck was founded with a simple mission: to provide reliable, professional CARB compliance services that trucking companies can depend on. Based in Ontario, California, we've built our reputation on quality service and technical expertise.
                </p>
                <p>
                  Our founder, with over a decade of experience in the diesel industry, recognized the growing need for specialized CARB compliance services in Southern California. The increasing complexity of emissions regulations meant that trucking companies needed a trusted partner who could navigate these requirements efficiently.
                </p>
                <p>
                  Today, we serve hundreds of trucks throughout San Bernardino County and surrounding areas, providing mobile emissions testing, compliance certification, and comprehensive fleet management services. Our commitment to excellence and customer satisfaction has made us the preferred choice for trucking companies of all sizes.
                </p>
              </div>
            </div>
            <div>
              <div className="relative rounded-xl overflow-hidden shadow-2xl mb-8">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/a728ddab7d71aca898b8aa1e2ed2cb3f.jpeg" 
                  alt="Professional handshake" 
                  className="w-full h-auto object-cover" 
                />
              </div>
              <div className="bg-blue-50 p-8 rounded-lg border border-blue-100">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Choose Us?</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Mobile Service</h4>
                      <p className="text-gray-600 text-sm">We come to your location for maximum convenience</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Skilled Technicians</h4>
                      <p className="text-gray-600 text-sm">Experienced CARB compliance and EPA specialists</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Emergency Service</h4>
                      <p className="text-gray-600 text-sm">Availability for urgent compliance needs</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Competitive Pricing</h4>
                      <p className="text-gray-600 text-sm">Fair, transparent pricing with fleet discounts</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Track Record</h2>
            <h3 className="text-2xl text-gray-600">Numbers That Speak for Themselves</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-3xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Values</h2>
            <h3 className="text-2xl text-gray-600">What Drives Us Every Day</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 border border-gray-100 rounded-lg bg-gray-50 bg-opacity-50"
              >
                <SafeIcon icon={value.icon} className="text-4xl text-blue-600 mb-4 mx-auto block" />
                <h4 className="text-xl font-semibold text-gray-800 mb-4">{value.title}</h4>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Expertise & Experience</h2>
            <h3 className="text-2xl text-gray-600">Professional and Experienced</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {certifications.map((cert, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center space-x-4 bg-white p-6 rounded-lg shadow-md"
              >
                <SafeIcon icon={FiAward} className="text-3xl text-blue-600" />
                <div>
                  <h4 className="text-lg font-semibold text-gray-800">{cert}</h4>
                  <p className="text-gray-600 text-sm">Professional and experienced</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Service Area</h2>
            <h3 className="text-2xl text-gray-600">Serving Southern California</h3>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 p-8 rounded-lg text-center border border-blue-100">
              <SafeIcon icon={FiTruck} className="text-5xl text-blue-600 mb-6 mx-auto block" />
              <h4 className="text-2xl font-bold text-gray-800 mb-4">
                Mobile Service Throughout San Bernardino County
              </h4>
              <p className="text-lg text-gray-600 mb-6">
                We provide mobile CARB compliance services throughout Southern California, with primary service areas including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  "Ontario", "Rancho Cucamonga", "Upland", "Fontana", 
                  "Chino", "Pomona", "San Bernardino", "Riverside"
                ].map((city, index) => (
                  <div key={index} className="text-gray-700 font-medium">
                    {city}, CA
                  </div>
                ))}
              </div>
              <p className="text-gray-600 mb-6">
                Don't see your city listed? We may still be able to serve your area. Contact us to discuss your specific location and needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:9092842738" 
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiPhone} />
                  <span>Call (909) 284-2738</span>
                </a>
                <a 
                  href="https://share.google/3y2ABFaMxbdz6hzaZ" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiMapPin} />
                  <span>View on Google Maps</span>
                  <SafeIcon icon={FiExternalLink} className="text-sm" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Work Together?</h2>
          <h3 className="text-2xl mb-8">Experience the Ramirez Difference</h3>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Join hundreds of satisfied customers who trust Ramirez Mobile Clean Truck for their CARB compliance needs. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:9092842738" 
              className="bg-yellow-500 text-blue-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg"
            >
              Call (909) 284-2738
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-blue-900 transition-colors text-lg"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;